import BaseComponent from './BaseComponent';
import { getElementFromEvent } from '../utils';
import gsap from 'gsap';
import { getKeyboardFocusableElements, trapFocus } from '../utils';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import throttle from 'lodash/throttle';

class Menu extends BaseComponent {
	constructor(element) {
		super();

		this.element = element;

		this.breakpoint = window.matchMedia('(min-width: 992px)');
		this.parser = new DOMParser();

		// State
		this.isOpen = false;
		this.firstLoad = true;
		this.trapFocus = null;
		this.mainMenuTrapFocus = null;
		this.lastScrollY = 0;
		this.scrollAnchor = window.scrollY;
		this.menuIsVisible = true;
		this.lastScrollDirection = null;

		this.element.addEventListener('click', (e) => {
			const button = getElementFromEvent(e, '[data-menu-toggle]');

			if (!button) {
				return;
			}

			this.isOpen ? this.close() : this.open();
		});

		this.element.addEventListener('click', (e) => {
			if (this.breakpoint.matches) {
				return;
			}

			e.preventDefault();

			const subMenu = getElementFromEvent(e, '.menu-item-has-children');
			const subMenuLink = getElementFromEvent(e, '.menu-item-has-children');

			if (!subMenuLink) {
				return;
			}

			this.toggleSubMenu(subMenu);
		});

		this.element.addEventListener('click', (e) => {
			if (this.breakpoint.matches) {
				return;
			}
			const menuGlass = getElementFromEvent(e, '[data-menu-glass]');

			if (menuGlass && this.isOpen) {
				this.close();
			}
		});

		document.addEventListener('leave', (e) => {
			if (e.detail?.next?.url && this.isOpen) {
				this.close();
			}
		});

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				this.close();
			}
		});

		document.addEventListener('bless', (e) => {
			if (this.firstLoad) {
				this.firstLoad = false;
				this.init();
				return;
			}

			this.replaceContent(e.detail?.next?.html);
			this.init();
		});

		document.addEventListener(
			'scroll',
			throttle(() => {
				if (!this.isOpen) {
					this.checkScroll();
				}
			}, 300)
		);

		this.breakpoint.addEventListener('change', (e) => {
			if (e.matches) {
				this.close();
			}

			const subMenus = this.element.querySelectorAll('.menu-item-has-children');
			subMenus.forEach((subMenu) => {
				subMenu.classList.remove('is-open');
				const subMenuElement = subMenu.querySelector('.sub-menu');
				subMenuElement.removeAttribute('style');
				gsap.set([subMenu, subMenuElement], { clearProps: 'all' });
			});
			this.trapFocus?.remove();
			this.trapFocus = null;
			this.isOpen = false;
			this.menuToggle.setAttribute('aria-expanded', 'false');
		});
	}

	init() {
		this.menuMobile = this.element.querySelector('[data-menu-mobile]');
		this.menuToggle = this.element.querySelector('[data-menu-toggle]');
		this.bg = this.element.querySelector('[data-menu-bg]');
		this.glass = this.element.querySelector('[data-menu-glass]');
		this.menuContent = this.element.querySelector('[data-menu-content]');
		this.subMenus = this.element.querySelectorAll('.menu-item-has-children');
		this.menuBlur = document.querySelector('[data-menu-blur]');

		if (this.menuBlur) {
			this.menuBlur.style.height = `${this.element.offsetHeight}px`;

			window.addEventListener('resize', () => {
				this.menuBlur.style.height = `${this.element.offsetHeight}px`;
			});
		}
	}

	replaceContent(html) {
		if (!html) {
			return;
		}

		const parsed = this.parser.parseFromString(html, 'text/html');
		const newMenu = parsed.querySelector('[data-menu] [data-replace-content]');

		if (!newMenu) {
			return;
		}

		this.menuContent.replaceWith(newMenu);
	}

	open() {
		if (this.isOpen) {
			return;
		}

		const menuItems = this.element.querySelectorAll('.c-menu__menu > .menu-item');
		const menuButton = this.element.querySelector('.menu-contact__button');
		const languageSwitcher = this.element.querySelector('.c-language-switcher');
		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.isOpen = true;
		this.element.classList.add('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'true');

		this.trapMainMenu();

		disablePageScroll();

		gsap.set([menuItems, languageSwitcher, menuButton], {
			y: 10,
			autoAlpha: 0,
		});

		tl.to(this.glass, {
			autoAlpha: 1,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
			height: '100vh',
		});

		tl.to(
			this.bg,
			{
				autoAlpha: 1,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
			},
			'<'
		);

		tl.to([menuItems, languageSwitcher, menuButton], {
			y: 0,
			autoAlpha: 1,
			stagger: prefersReducedMotion ? 0 : 0.1,
			duration: prefersReducedMotion ? 0 : 0.8,
			ease: 'power4.out',
		});

		return tl;
	}

	close() {
		if (!this.isOpen) {
			return;
		}

		this.isOpen = false;
		this.element.classList.remove('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'false');

		this.trapFocus?.remove();
		this.trapFocus = null;
		this.mainMenuTrapFocus?.remove();
		this.mainMenuTrapFocus = null;

		enablePageScroll();

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		tl.to(this.bg, {
			autoAlpha: 0,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			this.glass,
			{
				autoAlpha: 0,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
			},
			'<'
		);

		return tl;
	}

	trapMainMenu() {
		if (this.mainMenuTrapFocus) {
			this.mainMenuTrapFocus.remove();
		}

		const languageSwitcher = this.element.querySelector('.c-language-switcher');

		this.mainMenuTrapFocus = trapFocus(this.element, [
			this.menuToggle,
			this.element.querySelector('.c-menu__logo-mobile'),
			...getKeyboardFocusableElements(this.element.querySelector('.c-menu__menu')),
			...(languageSwitcher ? getKeyboardFocusableElements(languageSwitcher) : []),
		]);
	}

	checkScroll() {
		const currentScrollY = window.scrollY;
		const scrollingDown = currentScrollY > this.lastScrollY;
		const scrollThreshold = 200;
		const currentScrollDirection = scrollingDown ? 'down' : 'up';
		const menuElement = this.element;

		if (this.lastScrollDirection !== null && currentScrollDirection !== this.lastScrollDirection) {
			this.scrollAnchor = this.lastScrollY;
		}

		if (currentScrollDirection === 'down') {
			if (this.menuIsVisible && currentScrollY > this.scrollAnchor + scrollThreshold) {
				this.menuIsVisible = false;
				this.scrollAnchor = currentScrollY;

				gsap.to([menuElement, this.menuBlur], {
					y: '-100%',
					autoAlpha: 0,
					duration: 0.3,
					ease: 'power4.out',
				});
			}
		} else if (currentScrollDirection === 'up') {
			if (!this.menuIsVisible && currentScrollY < this.scrollAnchor - scrollThreshold) {
				this.menuIsVisible = true;
				this.scrollAnchor = currentScrollY;

				gsap.to([menuElement, this.menuBlur], {
					y: '0%',
					duration: 0.3,
					onComplete: () => {
						gsap.to([menuElement, this.menuBlur], {
							autoAlpha: 1,
							duration: 1,
							ease: 'power4.out',
						});
					},
				});
			}
		}

		this.lastScrollY = currentScrollY;
		this.lastScrollDirection = currentScrollDirection;
	}

	toggleSubMenu(subMenu) {
		if (!subMenu) {
			return;
		}

		if (subMenu.classList.contains('is-open')) {
			this.closeSubMenu(subMenu);
		} else {
			this.openSubMenu(subMenu);
		}
	}

	openSubMenu(subMenu) {
		subMenu.classList.add('is-open');
		subMenu.setAttribute('aria-expanded', 'true');

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const subMenuElement = subMenu.querySelector('.sub-menu');

		tl.to(
			subMenuElement,
			{
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'power2.in',
				maxHeight: subMenuElement.scrollHeight,
			},
			'<'
		);
		tl.to(subMenuElement, {
			duration: prefersReducedMotion ? 0 : 0.6,
			ease: 'none',
			autoAlpha: 1,
		});

		this.trapSubMenu(subMenu);
	}

	closeSubMenu(subMenu) {
		this.trapFocus?.remove();
		this.trapFocus = null;

		if (this.isOpen) {
			this.trapMainMenu();
		}
		subMenu.classList.remove('is-open');
		subMenu.setAttribute('aria-expanded', 'false');

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const subMenuElement = subMenu.querySelector('.sub-menu');

		tl.to(
			subMenuElement,
			{
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
				autoAlpha: 0,
			},
			'<'
		);
		tl.to(subMenuElement, {
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'power2.out',
			maxHeight: 0,
		});
	}

	trapSubMenu(subMenu) {
		if (this.trapFocus) {
			this.trapFocus.remove();
			this.trapFocus = null;
		}
		if (this.mainMenuTrapFocus) {
			this.mainMenuTrapFocus.remove();
		}
		this.trapFocus = trapFocus(subMenu, [
			subMenu.querySelector('.menu-item a'),
			...getKeyboardFocusableElements(subMenu),
		]);
	}
}

export const menu = new Menu(document.querySelector('[data-menu]'));
